import { useCallback, useRef, useState } from 'react';
import type { JSONContent } from '@tiptap/react';
import { CalendarClock, CalendarDays, Check, CornerDownRight, Layers, Pencil, Tag } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EntitySelect } from '@/components/EntitySelect';
import { MultiPopover } from '@/components/ui/multi-popover';
import { Input } from '@/components/ui/input';
import { Label as FieldLabel } from '@/components/ui/label';
import { BlockContent, BlockEditor, type BlockEditorHandle } from '@/components/ui/block-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { PRIORITIES, type Priority } from '../constants';
import {
  labelsHooks,
  modulesHooks,
  projectMembersHooks,
  statesHooks,
  useCreateWorkItem,
  workItemTypesHooks,
} from '../queries';
import { uploadInlineImage } from '../api';
import { makeWorkItemSearch } from '../select';
import type { Label, Module, Project, ProjectMember, State, WorkItemType } from '../types';
import { MemberPicker } from './MemberPicker';
import { WorkItemTypeIcon } from './WorkItemTypeIcon';

function rows<T>(raw: { results: T[] } | T[] | undefined): T[] {
  return (Array.isArray(raw) ? raw : (raw?.results ?? [])) as T[];
}

export function WorkItemCreateDialog({
  project,
  open,
  onOpenChange,
}: {
  project: Project;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [createMore, setCreateMore] = useState(false);
  const [formKey, setFormKey] = useState(0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create new work item</DialogTitle>
        </DialogHeader>
        <CreateForm
          key={formKey}
          project={project}
          createMore={createMore}
          onCreateMoreChange={setCreateMore}
          onDone={(again) => (again ? setFormKey((k) => k + 1) : onOpenChange(false))}
          onDiscard={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function CreateForm({
  project,
  createMore,
  onCreateMoreChange,
  onDone,
  onDiscard,
}: {
  project: Project;
  createMore: boolean;
  onCreateMoreChange: (v: boolean) => void;
  onDone: (again: boolean) => void;
  onDiscard: () => void;
}) {
  const create = useCreateWorkItem();
  const states = rows<State>(statesHooks.useList({ project: project.id }).data);
  const types = rows<WorkItemType>(workItemTypesHooks.useList({ project: project.id }).data);
  const labels = rows<Label>(labelsHooks.useList({ project: project.id }).data);
  const modules = rows<Module>(modulesHooks.useList({ project: project.id }).data);
  // Assignees come from the project's members only — not the whole org.
  const membersQuery = projectMembersHooks.useList({ project: project.id });
  const projectMembers = rows<ProjectMember>(membersQuery.data);

  const defaultType = types.find((t) => t.is_default) ?? types[0];
  const defaultState = states.find((s) => s.is_default) ?? states[0];

  const [title, setTitle] = useState('');
  // Description is view-first like the drawer: read/placeholder until the pencil
  // toggles edit mode. Edits are mirrored into refs so submit() can flush the
  // editor and read the latest html+json synchronously; `desc` state drives the
  // read view.
  const [editingDesc, setEditingDesc] = useState(false);
  const [desc, setDesc] = useState<{ html: string; json: JSONContent | null }>({
    html: '',
    json: null,
  });
  const descHtml = useRef('');
  const descJson = useRef<JSONContent | null>(null);
  const editorRef = useRef<BlockEditorHandle>(null);
  const [typeId, setTypeId] = useState<number | null>(null);
  const [stateId, setStateId] = useState<number | null>(null);
  const [priority, setPriority] = useState<Priority>('none');
  const [assignees, setAssignees] = useState<number[]>([]);
  const [labelIds, setLabelIds] = useState<number[]>([]);
  const [startDate, setStartDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [moduleId, setModuleId] = useState<number | null>(null);
  const [parentId, setParentId] = useState('');

  const parentSearch = useCallback(() => makeWorkItemSearch(project.id), [project.id])();

  function submit() {
    if (!title.trim()) return;
    // Persist any edit still waiting on the editor's debounce before reading.
    editorRef.current?.flush();
    create.mutate(
      {
        project: project.id,
        type: typeId ?? defaultType?.id ?? null,
        title: title.trim(),
        description: descHtml.current || undefined,
        description_json: descJson.current ?? undefined,
        state: stateId ?? defaultState?.id ?? null,
        priority,
        assignees,
        labels: labelIds,
        start_date: startDate || null,
        due_date: dueDate || null,
        module: moduleId,
        parent: parentId ? Number(parentId) : null,
      },
      { onSuccess: () => onDone(createMore) },
    );
  }

  const typeValue = typeId != null ? String(typeId) : defaultType ? String(defaultType.id) : undefined;
  const stateValue = stateId != null ? String(stateId) : defaultState ? String(defaultState.id) : undefined;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {/* Scrollable body — keeps the footer reachable with a long description. */}
      <div className="-mr-1 min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
      {/* Breadcrumb: project ▸ type */}
      <div className="flex items-center gap-2">
        <span className="bg-muted/60 flex items-center gap-1.5 rounded-md px-2 py-1 text-sm">
          <span>{project.icon || '📁'}</span> {project.name}
        </span>
        <span className="text-muted-foreground">›</span>
        <Select value={typeValue} onValueChange={(v) => setTypeId(Number(v))}>
          <SelectTrigger className="h-8 w-40"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            {types.map((t) => (
              <SelectItem key={t.id} value={String(t.id)}>
                <span className="flex items-center gap-2">
                  <WorkItemTypeIcon type={t} /> {t.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title"
        className="h-auto border-0 px-0 text-lg font-semibold shadow-none focus-visible:ring-0"
      />

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <FieldLabel>Description</FieldLabel>
          <button
            type="button"
            aria-label={editingDesc ? 'Done editing description' : 'Edit description'}
            title={editingDesc ? 'Done' : 'Edit'}
            onClick={() => {
              if (editingDesc) editorRef.current?.flush();
              setEditingDesc((v) => !v);
            }}
            className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1"
          >
            {editingDesc ? <Check className="size-4" /> : <Pencil className="size-3.5" />}
          </button>
        </div>
        {editingDesc ? (
          <BlockEditor
            ref={editorRef}
            compact
            placeholder="Add a description…"
            value={desc.json}
            html={desc.html}
            uploadImage={(file) => uploadInlineImage(project.id, file)}
            onSave={({ html, json }) => {
              descHtml.current = html;
              descJson.current = json;
              setDesc({ html, json });
            }}
          />
        ) : desc.html ? (
          <BlockContent value={desc.json} html={desc.html} />
        ) : (
          <button
            type="button"
            onClick={() => setEditingDesc(true)}
            className="text-muted-foreground hover:text-foreground text-sm"
          >
            Click to add description
          </button>
        )}
      </div>

      {/* Property pills */}
      <div className="flex flex-wrap items-center gap-2 border-t pt-3">
        <Select value={stateValue} onValueChange={(v) => setStateId(Number(v))}>
          <SelectTrigger className="h-8 w-auto gap-1"><SelectValue placeholder="State" /></SelectTrigger>
          <SelectContent>
            {states.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={priority} onValueChange={(v) => setPriority(v as Priority)}>
          <SelectTrigger className="h-8 w-auto gap-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>

        <MemberPicker
          members={projectMembers}
          selected={assignees}
          onToggle={(id) =>
            setAssignees((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]))
          }
          onClear={() => setAssignees([])}
          loading={membersQuery.isLoading}
        />

        <MultiPopover
          icon={Tag}
          label="Labels"
          count={labelIds.length}
          options={labels.map((l) => ({ id: l.id, name: l.name, color: l.color }))}
          selected={labelIds}
          onToggle={(id) =>
            setLabelIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]))
          }
        />

        <DatePill icon={CalendarDays} label="Start" value={startDate} onChange={setStartDate} />
        <DatePill icon={CalendarClock} label="Due" value={dueDate} onChange={setDueDate} />

        <Select
          value={moduleId != null ? String(moduleId) : 'none'}
          onValueChange={(v) => setModuleId(v === 'none' ? null : Number(v))}
        >
          <SelectTrigger className="h-8 w-auto gap-1">
            <Layers className="size-3.5" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No module</SelectItem>
            {modules.map((m) => <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1">
          <CornerDownRight className="text-muted-foreground size-3.5" />
          <div className="w-44">
            <EntitySelect
              queryKey={`parent-${project.id}`}
              value={parentId}
              onChange={setParentId}
              search={parentSearch}
              placeholder="Add parent"
            />
          </div>
        </div>
      </div>
      </div>

      {/* Footer — pinned below the scroll area so it's always reachable. */}
      <div className="flex items-center justify-end gap-3 border-t pt-3">
        <label htmlFor="wi-create-more" className="text-muted-foreground mr-auto flex items-center gap-2 text-sm">
          <Switch id="wi-create-more" checked={createMore} onCheckedChange={onCreateMoreChange} />
          Create more
        </label>
        <Button variant="outline" onClick={onDiscard}>Discard</Button>
        <Button onClick={submit} disabled={!title.trim() || create.isPending}>
          {create.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

function DatePill({
  icon: Icon,
  label,
  value,
  onChange,
}: {
  icon: typeof CalendarDays;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label
      className={cn(
        'flex h-8 items-center gap-1.5 rounded-md border px-2 text-sm',
        value ? '' : 'text-muted-foreground',
      )}
    >
      <Icon className="size-3.5" />
      {value || label}
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-0 opacity-0"
        aria-label={label}
      />
    </label>
  );
}
